/**
 * Cosmo Crypt - WebAuthn & FIDO2 Passkeys Service
 * Provides hardware-backed biometric (Touch ID / Face ID / Windows Hello) and security key (YubiKey) authentication.
 * 
 * Supports:
 * - Domain-aware RP ID (works seamlessly on localhost and production)
 * - Base64URL <-> ArrayBuffer bidirectional conversions
 * - Credential Registration & Authentication
 * - Biometric App Lock Quick-Unlock
 */

// --- Base64URL Utility Functions ---
export function bufferToBase64URL(buffer) {
    if (!buffer) return '';
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

export function base64URLToBuffer(base64url) {
    if (!base64url) return new Uint8Array(0).buffer;
    let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) {
        base64 += '=';
    }
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

// --- Get Safe Relying Party ID for current origin ---
export function getSafeRPID() {
    const hostname = window.location.hostname;
    // On localhost or local IP, return exact hostname
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local')) {
        return hostname;
    }
    // Production domain support (e.g. cosmocrypt.com)
    if (hostname.endsWith('cosmocrypt.com')) {
        return 'cosmocrypt.com';
    }
    // Firebase Hosting domains (e.g. cosmo-crypt.web.app, cosmo-crypt.firebaseapp.com)
    // Note: web.app and firebaseapp.com are on the Public Suffix List (PSL), so browsers require full hostname
    return hostname;
}

// --- Check Hardware & Biometric Support ---
export async function isPasskeySupported() {
    try {
        if (!window.isSecureContext && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
            console.warn("[WebAuthn] Context is not secure (requires HTTPS or localhost)");
            return false;
        }
        if (!window.PublicKeyCredential) {
            return false;
        }
        if (typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
            const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
            return available;
        }
        return true;
    } catch (e) {
        console.warn("[WebAuthn] Support check error:", e);
        return false;
    }
}

// --- Register a New Passkey / Hardware Token ---
export async function registerPasskey({ userEmail, userId, customChallenge, deviceName }) {
    if (!window.PublicKeyCredential) {
        throw new Error("WebAuthn / Passkeys are not supported on this browser.");
    }

    // Process server-provided challenge or fallback to CSPRNG bytes
    let challengeBytes;
    if (customChallenge) {
        challengeBytes = typeof customChallenge === 'string'
            ? base64URLToBuffer(customChallenge)
            : (customChallenge instanceof ArrayBuffer ? customChallenge : (customChallenge.buffer || customChallenge));
    } else {
        challengeBytes = crypto.getRandomValues(new Uint8Array(32)).buffer;
    }

    const userHandle = new TextEncoder().encode(userId || userEmail || 'cosmo-user');

    const creationOptions = {
        challenge: challengeBytes,
        rp: {
            name: "Cosmo Crypt",
            id: getSafeRPID()
        },
        user: {
            id: userHandle,
            name: userEmail || "user@cosmocrypt.com",
            displayName: (userEmail || "Cosmo User").split('@')[0]
        },
        pubKeyCredParams: [
            { alg: -7, type: "public-key" },   // ES256 (ECDSA P-256)
            { alg: -257, type: "public-key" }, // RS256 (RSA 2048 - Windows Hello TPM Primary)
            { alg: -8, type: "public-key" },   // Ed25519
            { alg: -37, type: "public-key" },  // PS256
            { alg: -258, type: "public-key" }, // RS384
            { alg: -259, type: "public-key" }  // RS512
        ],
        authenticatorSelection: {
            authenticatorAttachment: undefined, // Supports platform (Windows Hello / Touch ID) and cross-platform (YubiKey)
            userVerification: "preferred",
            residentKey: "preferred",
            requireResidentKey: false
        },
        timeout: 60000,
        attestation: "none"
    };

    try {
        const credential = await navigator.credentials.create({
            publicKey: creationOptions
        });

        if (!credential) {
            throw new Error("Passkey registration was cancelled by user.");
        }

        // Extract DER / SPKI Public Key (available in modern W3C WebAuthn engines)
        let publicKeyBase64 = "";
        if (credential.response && typeof credential.response.getPublicKey === 'function') {
            const pkBuffer = credential.response.getPublicKey();
            if (pkBuffer) publicKeyBase64 = bufferToBase64URL(pkBuffer);
        }
        if (!publicKeyBase64 && credential.response && credential.response.attestationObject) {
            publicKeyBase64 = bufferToBase64URL(credential.response.attestationObject);
        }

        const transports = (credential.response && typeof credential.response.getTransports === 'function')
            ? credential.response.getTransports()
            : ["internal"];

        const isPlatform = (credential.authenticatorAttachment === 'platform') || (!transports.includes('usb') && !transports.includes('nfc'));
        const nowIso = new Date().toISOString();

        return {
            id: credential.id,
            publicKey: publicKeyBase64,
            counter: 0,
            deviceName: deviceName || (navigator.userAgent.includes('Mac') ? 'MacBook Touch ID' : navigator.userAgent.includes('Windows') ? 'Windows Hello' : 'FIDO2 Security Key'),
            deviceType: isPlatform ? "singleDevice" : "multiDevice",
            backedUp: false,
            transports: transports.length > 0 ? transports : ["internal"],
            createdAt: nowIso,
            lastUsedAt: nowIso
        };
    } catch (err) {
        if (err.name === 'NotAllowedError') {
            throw new Error("Passkey prompt was cancelled or timed out.");
        }
        throw err;
    }
}

// --- Authenticate / Unlock with Passkey ---
export async function authenticatePasskey({ customChallenge, allowedCredentialIds = [] } = {}) {
    if (!window.PublicKeyCredential) {
        throw new Error("WebAuthn / Passkeys are not supported on this browser.");
    }

    let challengeBytes;
    if (customChallenge) {
        challengeBytes = typeof customChallenge === 'string'
            ? base64URLToBuffer(customChallenge)
            : (customChallenge instanceof ArrayBuffer ? customChallenge : (customChallenge.buffer || customChallenge));
    } else {
        challengeBytes = crypto.getRandomValues(new Uint8Array(32)).buffer;
    }

    const allowCredentials = (allowedCredentialIds || []).filter(Boolean).map(id => ({
        id: typeof id === 'string' ? base64URLToBuffer(id) : id,
        type: 'public-key',
        transports: ['internal', 'hybrid', 'usb', 'nfc', 'ble']
    }));

    const requestOptions = {
        challenge: challengeBytes,
        rpId: getSafeRPID(),
        allowCredentials: allowCredentials.length > 0 ? allowCredentials : undefined,
        userVerification: "preferred",
        timeout: 60000
    };

    try {
        const assertion = await navigator.credentials.get({
            publicKey: requestOptions
        });

        if (!assertion) {
            throw new Error("Passkey verification was cancelled.");
        }

        return {
            id: assertion.id,
            rawId: bufferToBase64URL(assertion.rawId),
            type: assertion.type,
            response: {
                clientDataJSON: bufferToBase64URL(assertion.response.clientDataJSON),
                authenticatorData: bufferToBase64URL(assertion.response.authenticatorData),
                signature: bufferToBase64URL(assertion.response.signature),
                userHandle: assertion.response.userHandle ? bufferToBase64URL(assertion.response.userHandle) : null
            }
        };
    } catch (err) {
        if (err.name === 'NotAllowedError') {
            throw new Error("Biometric / Security key verification was cancelled.");
        }
        throw err;
    }
}

// --- Quick Biometric Lock Screen Sealing ---
const BIO_LOCK_STORAGE_KEY = '__cc_bio_unwrapped_key';

export async function storeBiometricVaultKey(pinOrPassword) {
    try {
        const isAvail = await isPasskeySupported();
        if (!isAvail) return false;

        // Securely store encrypted PIN for quick biometric unlock
        const secret = (window._getAppSecret && typeof window._getAppSecret === 'function') 
            ? window._getAppSecret() 
            : (window.parent && typeof window.parent._getAppSecret === 'function' ? window.parent._getAppSecret() : null);
        if (!secret) return false;

        if (typeof CryptoJS !== 'undefined') {
            const encrypted = CryptoJS.AES.encrypt(pinOrPassword, secret).toString();
            sessionStorage.setItem(BIO_LOCK_STORAGE_KEY, encrypted);
            return true;
        }
        return false;
    } catch (e) {
        console.warn("Could not store biometric vault cache:", e);
        return false;
    }
}

export async function retrieveBiometricVaultKey() {
    try {
        const encrypted = sessionStorage.getItem(BIO_LOCK_STORAGE_KEY);
        if (!encrypted) return null;

        // Require biometric touch assertion
        await authenticatePasskey();

        const secret = (window._getAppSecret && typeof window._getAppSecret === 'function') 
            ? window._getAppSecret() 
            : (window.parent && typeof window.parent._getAppSecret === 'function' ? window.parent._getAppSecret() : null);
        if (!secret) return null;

        if (typeof CryptoJS !== 'undefined') {
            const bytes = CryptoJS.AES.decrypt(encrypted, secret);
            const decrypted = bytes.toString(CryptoJS.enc.Utf8);
            return decrypted || null;
        }
        return null;
    } catch (e) {
        console.warn("Biometric quick unlock failed:", e);
        throw e;
    }
}

export function clearBiometricVaultKey() {
    sessionStorage.removeItem(BIO_LOCK_STORAGE_KEY);
}

// Attach globally for suite components
if (typeof window !== 'undefined') {
    window.CosmoWebAuthn = {
        isPasskeySupported,
        registerPasskey,
        authenticatePasskey,
        storeBiometricVaultKey,
        retrieveBiometricVaultKey,
        clearBiometricVaultKey,
        bufferToBase64URL,
        base64URLToBuffer
    };
}
